import { useState, useEffect, useCallback, useRef, useMemo, type PointerEvent as ReactPointerEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import { CollabUpgradeGate } from '@/components/feature/CollabUpgradeGate';
import UserAvatar from '@/components/feature/UserAvatar';
import { teamsApi, songsApi, type TeamContext } from '@/api/songs';
import { sessionsApi } from '@/api/sessions';
import { subscriptionApi } from '@/api/subscriptions';
import { useSessionWS, type ConnectionState } from '@/hooks/useSessionWS';
import { useAuth } from '@/hooks/useAuth';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useScrollAnchors } from '@/hooks/useScrollAnchors';
import { useFontSizeStore, SIZE_VALUES } from '@/stores/fontSizeStore';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { useMinistryStore } from '@/stores/ministryStore';
import { DARK_THEME, useThemeStore } from '@/stores/themeStore';
import SetlistSidebar from './components/SetlistSidebar';
import LoadSetlistPicker from './components/LoadSetlistPicker';
import { listEvents } from '@/api/events';
import type {
  Session,
  SessionMember,
  SessionSnapshot,
  WsEvent,
  ControlEventType,
  ScrollAnchor,
  SetlistWs,
  SetlistWsItem,
} from '@/types/session';
import type { SongListItem, Song, Team, Section } from '@/types/song';
import { transposeKey, semitonesFromTo, transposeChordToken } from '@/utils/transposeChords';
import { formatSongKey } from '@/utils/songKey';
import { applyViewMode, isCapoCompatible, getCapoSuggestions, type ViewMode } from '@/utils/instrumentRenderer';
import { tokeniseLine } from '@/utils/parseChords';
import { isLineDoubleTap, type LineTapCandidate } from '@/utils/lineHighlight';
import { CHORD_SHEET_PALETTES, type ChordSheetPalette } from '@/utils/chordSheetTheme';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Signal {
  type: string;
  label: string;
  icon: string;
  color: string;
  persistent: boolean;
}

interface RecoveredRoomState {
  currentSongId: string | null;
  activeSetlist: SetlistWs | null;
  setlistIndex: number | null;
  isPaused: boolean;
  keyOverride: string;
  bpmOverride: number | null;
  currentPosition: number;
  scrollAnchor: ScrollAnchor | null;
  leaderFontSize: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  members: SessionMember[];
  viewMode: ViewMode;
  capoFret: number;
}

interface SongLineTarget {
  sectionId: string;
  lineIndex: number;
}

interface ActiveLineHighlight extends SongLineTarget {
  highlightId: string;
}

interface LinePointerStart extends SongLineTarget {
  pointerId: number;
  x: number;
  y: number;
}

// eslint-disable-next-line react-refresh/only-export-components
export function mapSessionSnapshot(snapshot: SessionSnapshot): RecoveredRoomState {
  return {
    currentSongId: snapshot.session.current_song_id,
    activeSetlist: snapshot.setlist,
    setlistIndex: snapshot.session.current_setlist_index,
    isPaused: snapshot.session.status === 'PAUSED',
    keyOverride: snapshot.session.key_override,
    bpmOverride: snapshot.session.bpm_override,
    currentPosition: snapshot.session.current_position,
    scrollAnchor: snapshot.session.scroll_anchor,
    leaderFontSize: snapshot.session.leader_font_size,
    members: snapshot.members,
    viewMode: snapshot.participant.view_mode,
    capoFret: snapshot.participant.capo_fret ?? 1,
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function getSetlistInitialSelection(items: SetlistWsItem[]) {
  const firstItem = items[0];
  return {
    currentSongId: firstItem?.song_id ?? null,
    setlistIndex: firstItem ? 0 : null,
    keyOverride: firstItem?.key_override ?? '',
    isPaused: false,
  };
}

// Keep the default team aligned with LibraryPage, which also selects the first team.
// eslint-disable-next-line react-refresh/only-export-components
export function getDefaultCollabTeamId(teams: Pick<Team, 'id'>[]): string | null {
  return teams[0]?.id ?? null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function teamHasCollabAccess(
  teamId: string | null,
  teamContexts: Pick<TeamContext, 'team_id' | 'owner_plan_name'>[],
): boolean {
  const planName = teamContexts.find((context) => context.team_id === teamId)?.owner_plan_name;
  return Boolean(
    planName &&
    (planName.includes('Group') || planName.includes('Unlimited') || planName.includes('ULT')),
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function getScrollRestoreTarget(
  anchor: ScrollAnchor | null,
  currentPosition: number,
  anchorAvailable = true,
) {
  if (anchor && anchorAvailable) return { type: 'anchor' as const, anchor };
  if (currentPosition > 0) return { type: 'position' as const, position: currentPosition };
  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function resetRoomScroll(container: HTMLDivElement | null) {
  if (container) container.scrollTop = 0;
}

export function CollabConnectionStatus({ state }: { state: ConnectionState }) {
  if (state === 'connected') return null;
  const label = state === 'synchronizing'
    ? 'Sincronizando sessão…'
    : state === 'reconnecting'
      ? 'Reconectando…'
      : 'Conectando à sessão…';
  return (
    <div role="status" aria-live="polite" className="px-4 py-2 text-center text-sm border-b border-white/10 bg-amber-900/20 text-amber-200">
      {label}
    </div>
  );
}

// ── Config ────────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export const SIGNALS: Record<string, Signal> = {
  change:    { type: 'change',    label: 'Mudança!',           icon: 'ri-arrow-right-circle-line', color: '#C8A96E', persistent: false },
  pause:     { type: 'pause',     label: 'Pausa!',             icon: 'ri-pause-circle-line',        color: '#7EB8A4', persistent: true  },
  drums:     { type: 'drums',     label: 'Só bateria!',        icon: 'ri-rhythm-line',              color: '#A97EC8', persistent: true  },
  'key-up':  { type: 'key-up',   label: 'Vai subir o tom!',   icon: 'ri-arrow-up-circle-line',     color: '#C87E7E', persistent: false },
  'key-down':{ type: 'key-down', label: 'Vai baixar o tom!',  icon: 'ri-arrow-down-circle-line',   color: '#7E9EC8', persistent: false },
  slow:      { type: 'slow',     label: 'Ralentando!',        icon: 'ri-speed-line',               color: '#7E9EC8', persistent: false },
};


const SECTION_LABELS: Record<string, string> = {
  INTRO: 'Intro', VERSE: 'Verso', PRE_CHORUS: 'Pré-Refrão',
  CHORUS: 'Refrão', BRIDGE: 'Ponte', OUTRO: 'Outro',
  INSTRUMENTAL: 'Instrumental', INTERLUDE: 'Interlúdio', TAG: 'Tag',
};

// ── SessionBanner ─────────────────────────────────────────────────────────────

export const SessionBanner = ({ signal, onDismiss }: { signal: Signal; onDismiss: () => void }) => (
  <div
    role="status"
    aria-live="assertive"
    className="flex items-center justify-center gap-3 py-4 text-lg font-bold shrink-0"
    style={{ backgroundColor: `${signal.color}20`, color: signal.color }}
  >
    <i className={`${signal.icon} text-2xl`}></i>
    <span>{signal.label}</span>
    {signal.persistent && (
      <button
        onClick={onDismiss}
        className="ml-3 text-sm opacity-50 hover:opacity-100 cursor-pointer transition-opacity"
        aria-label="Fechar aviso"
      >
        <i className="ri-close-line"></i>
      </button>
    )}
  </div>
);

// ── SongContent ───────────────────────────────────────────────────────────────

// Regex para detectar cifras (acordes)
// Suporta notações internacional e brasileira: C, Cm, C7, C7M, C7+, C°, Cº, CΔ7,
// C/E, Cm7(b5), Am7(5-), B7(13-), F7M/C, G6, Ebmaj7, Gsus4, etc.
const CHORD_RE = /(?<![A-Za-z])([A-G][b#]?(?:m(?:aj)?|dim|aug|[°º]|\u0394|sus[24]?|add\d+)?(?:\d+[M+]?)?(?:\([^)]*\))?(?:\/[A-G][b#]?)?)(?![a-z])/g;
const SINGLE_CHORD_RE = /^[A-G][b#]?(?:m(?:aj)?|dim|aug|[°º]|\u0394|sus[24]?|add\d+)?(?:\d+[M+]?)?(?:\([^)]*\))?(?:\/[A-G][b#]?)?$/;

// Detecta se uma linha é primariamente uma linha de cifras
const isChordLine = (line: string): boolean => {
  const normalizedLine = line.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
  if (!normalizedLine) return false;
  
  // Encontra todas as cifras na linha
  const chords = normalizedLine.match(CHORD_RE) || [];
  
  // Se não tem cifras, é letra
  if (chords.length === 0) return false;
  
  // Divide a linha em palavras
  const words = normalizedLine.split(/\s+/).filter(w => w.length > 0);
  
  // Se 60%+ das palavras são cifras, é linha de cifra
  // Isso evita falsos positivos como "E na plenitude" onde "E" é cifra mas resto é letra
  const chordWords = words.filter(word => SINGLE_CHORD_RE.test(word));
  const chordRatio = chordWords.length / Math.max(1, words.length);
  
  return chordRatio >= 0.6;
};

// Renderiza conteúdo com cifras em negrito e cor laranja avermelhada
// Annotation tags `{text}` são renderizadas como pills amber em todas as linhas.
const renderContentWithBoldChords = (
  content: string,
  sectionId: string,
  fontSize: string,
  sheetPalette: ChordSheetPalette,
  semitones: number,
  viewMode: ViewMode,
  capoFret: number | null,
  activeHighlight: ActiveLineHighlight | null,
  onLinePointerDown?: (event: ReactPointerEvent<HTMLDivElement>, target: SongLineTarget) => void,
  onLinePointerUp?: (event: ReactPointerEvent<HTMLDivElement>, target: SongLineTarget) => void,
  onLinePointerCancel?: (event: ReactPointerEvent<HTMLDivElement>) => void,
) => {
  const lines = content.split('\n');

  return lines.map((line, lineIdx) => {
    const isChord = isChordLine(line);
    const tokens = tokeniseLine(line);

    // Build inline JSX nodes for this line
    const nodes = tokens.map((token, ti) => {
      if (token.kind === 'annotation') {
        return (
          <span
            key={ti}
            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mx-0.5"
            style={{ backgroundColor: sheetPalette.annotationBackground, color: sheetPalette.annotationText, fontFamily: 'inherit' }}
          >
            {'{' + token.text + '}'}
          </span>
        );
      }
      if (token.kind === 'chord' && isChord) {
        let displayChord = token.text;
        if (semitones !== 0) displayChord = transposeChordToken(displayChord, semitones);
        if (viewMode !== 'STANDARD') displayChord = applyViewMode(displayChord, viewMode, capoFret ?? undefined);
        return <span key={ti}>{displayChord}</span>;
      }
      return <span key={ti}>{token.text}</span>;
    });

    return (
      <div
        key={`${sectionId}-${lineIdx}-${activeHighlight?.sectionId === sectionId && activeHighlight.lineIndex === lineIdx ? activeHighlight.highlightId : ''}`}
        className={activeHighlight?.sectionId === sectionId && activeHighlight.lineIndex === lineIdx ? 'collab-line-highlight' : undefined}
        onPointerDown={onLinePointerDown ? (event) => onLinePointerDown(event, { sectionId, lineIndex: lineIdx }) : undefined}
        onPointerUp={onLinePointerUp ? (event) => onLinePointerUp(event, { sectionId, lineIndex: lineIdx }) : undefined}
        onPointerCancel={onLinePointerCancel}
        style={{
          fontWeight: isChord ? 'bold' : 'normal',
          color: isChord ? sheetPalette.chord : sheetPalette.text,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          cursor: onLinePointerDown ? 'pointer' : undefined,
          touchAction: onLinePointerDown ? 'manipulation' : undefined,
        }}
      >
        {nodes}
      </div>
    );
  });
};

const SongContent = ({
  sections,
  sheetPalette,
  fontSize,
  semitones,
  viewMode,
  capoFret,
  activeHighlight,
  onLinePointerDown,
  onLinePointerUp,
  onLinePointerCancel,
}: {
  sections: Section[];
  sheetPalette: ChordSheetPalette;
  fontSize: string;
  semitones: number;
  viewMode: ViewMode;
  capoFret: number | null;
  activeHighlight: ActiveLineHighlight | null;
  onLinePointerDown?: (event: ReactPointerEvent<HTMLDivElement>, target: SongLineTarget) => void;
  onLinePointerUp?: (event: ReactPointerEvent<HTMLDivElement>, target: SongLineTarget) => void;
  onLinePointerCancel?: (event: ReactPointerEvent<HTMLDivElement>) => void;
}) => (
  <div className="flex flex-col gap-5 relative">
    {sections.map((section) => {
      const label = section.title || SECTION_LABELS[section.type] || section.type;
      return (
        <div
          key={section.id}
          className="relative"
          data-section-anchor={section.id}
          data-section-type={section.type}
        >
          <p style={{ color: sheetPalette.muted }} className="text-xs uppercase tracking-widest mb-2">{label}</p>
          <div style={{ color: sheetPalette.text }} className={`${fontSize} leading-loose font-mono`}>
            {renderContentWithBoldChords(
              section.content,
              section.id,
              fontSize,
              sheetPalette,
              semitones,
              viewMode,
              capoFret,
              activeHighlight,
              onLinePointerDown,
              onLinePointerUp,
              onLinePointerCancel,
            )}
          </div>
        </div>
      );
    })}
  </div>
);

// ── TeamSelector ─────────────────────────────────────────────────────────────

const TeamSelector = ({
  teams,
  selectedTeamId,
  onSelectTeam,
  theme,
}: {
  teams: Team[];
  selectedTeamId: string | null;
  onSelectTeam: (teamId: string) => void;
  theme: any;
}) => (
  <div className="flex-1 flex items-center justify-center">
    <div className="w-full max-w-md mx-auto px-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 flex items-center justify-center rounded-full transition-colors mx-auto mb-4" style={{ backgroundColor: theme.colors.bg.secondary }}>
          <i className="ri-team-line text-2xl" style={{ color: theme.colors.text.tertiary }}></i>
        </div>
        <h2 className="font-semibold text-xl mb-1" style={{ color: theme.colors.text.primary, fontFamily: "'Playfair Display', serif" }}>
          Escolha uma Equipe
        </h2>
        <p style={{ color: theme.colors.text.tertiary }} className="text-sm">Selecione a equipe para colaboração ao vivo</p>
      </div>

      <div className="flex flex-col gap-3">
        {teams.map((t) => (
          <button
            key={t.id}
            onClick={() => onSelectTeam(t.id)}
            className="flex items-center justify-between p-4 rounded-xl transition-colors cursor-pointer"
            style={{
              backgroundColor: selectedTeamId === t.id ? `${theme.colors.accent}20` : theme.colors.bg.secondary,
              borderColor: selectedTeamId === t.id ? `${theme.colors.accent}60` : theme.colors.border,
              color: theme.colors.text.primary,
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: selectedTeamId === t.id ? theme.colors.accent : theme.colors.text.tertiary
              }}></div>
              <span className="font-medium">{t.name}</span>
            </div>
            <span style={{ color: theme.colors.text.tertiary }} className="text-xs">{t.invite_code}</span>
          </button>
        ))}
      </div>

      <button
        onClick={() => selectedTeamId && onSelectTeam(selectedTeamId)}
        disabled={!selectedTeamId}
        className="w-full mt-6 py-3 font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: theme.colors.accent,
          color: theme.colors.bg.primary,
        }}
        onMouseEnter={(e) => {
          if (!selectedTeamId) return;
          e.currentTarget.style.backgroundColor = theme.colors.accentHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.accent;
        }}
      >
        Continuar →
      </button>
    </div>
  </div>
);

// ── Lobby ─────────────────────────────────────────────────────────────────────

const Lobby = ({
  team,
  sessions,
  onJoin,
  onCreate,
  onChangeTeam,
  theme,
}: {
  team: Team;
  sessions: Session[];
  onJoin: (s: Session) => void;
  onCreate: () => void;
  onChangeTeam: () => void;
  theme: any;
}) => (
  <div className="flex-1 flex items-center justify-center">
    <div className="w-full max-w-md mx-auto px-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 flex items-center justify-center rounded-full transition-colors mx-auto mb-4" style={{ backgroundColor: theme.colors.bg.secondary }}>
          <i className="ri-live-line text-2xl" style={{ color: theme.colors.text.tertiary }}></i>
        </div>
        <h2 className="font-semibold text-xl mb-1" style={{ color: theme.colors.text.primary, fontFamily: "'Playfair Display', serif" }}>
          Colaboração ao Vivo
        </h2>
        <div className="flex items-center justify-center gap-2 text-sm">
          <p style={{ color: theme.colors.text.tertiary }}>{team.name}</p>
          <button
            onClick={onChangeTeam}
            className="text-xs hover:opacity-70 transition-opacity"
            style={{ color: theme.colors.accent }}
            title="Trocar equipe"
          >
            <i className="ri-refresh-line"></i>
          </button>
        </div>
      </div>

      {sessions.length > 0 && (
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest mb-3" style={{ color: theme.colors.text.tertiary }}>Sessões ativas</p>
          <div className="flex flex-col gap-2">
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => onJoin(s)}
                className="flex items-center justify-between p-4 rounded-xl transition-colors cursor-pointer"
                style={{
                  backgroundColor: theme.colors.bg.secondary,
                  borderColor: theme.colors.border,
                  color: theme.colors.text.primary,
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                  <span className="text-sm">Sessão em andamento</span>
                </div>
                <span className="text-xs" style={{ color: theme.colors.accent }}>Entrar →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onCreate}
        className="w-full flex items-center justify-center gap-2 py-3 font-semibold rounded-xl transition-colors cursor-pointer"
        style={{
          backgroundColor: theme.colors.accent,
          color: theme.colors.bg.primary,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.accentHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.accent;
        }}
      >
        <i className="ri-add-line"></i>
        Criar nova sessão
      </button>
    </div>
  </div>
);

// ── CollabRoom ────────────────────────────────────────────────────────────────

const CollabRoom = ({
  session,
  team,
  songs,
  onEnd,
  onWsError,
  isEndingSession,
}: {
  session: Session;
  team: Team;
  songs: SongListItem[];
  onEnd: () => void;
  onWsError: (error: string | null) => void;
  isEndingSession: React.MutableRefObject<boolean>;
}) => {
  const { user } = useAuth();
  const theme = DARK_THEME;
  const sheetMode = useThemeStore((state) => state.mode);
  const sheetPalette = CHORD_SHEET_PALETTES[sheetMode];
  const { size: fontSize, increase: increaseFontSize, decrease: decreaseFontSize } = useFontSizeStore();
  const fontSizeClass = SIZE_VALUES[fontSize];
  const isLeader = session.leader_id === user?.id;
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [members, setMembers] = useState<SessionMember[]>(session.members);
  const [currentSongId, setCurrentSongId] = useState<string | null>(session.current_song_id);
  const [activeSetlist, setActiveSetlist] = useState<SetlistWs | null>(null);
  const [setlistIndex, setSetlistIndex] = useState<number | null>(null);
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [isPaused, setIsPaused] = useState(session.status === 'PAUSED');
  const [bpmOverride, setBpmOverride] = useState<number | null>(session.bpm_override);
  const [currentPosition, setCurrentPosition] = useState(session.current_position);
  const [activeSignal, setActiveSignal] = useState<Signal | null>(null);
  const [activeLineHighlight, setActiveLineHighlight] = useState<ActiveLineHighlight | null>(null);
  const [alertHistory, setAlertHistory] = useState<{ type: string; time: string }[]>([]);
  const [keyOverride, setKeyOverride] = useState<string>(session.key_override || '');
  const keyOverrideRef = useRef<string>(session.key_override || '');
  const currentSongRef = useRef<Song | null>(null);
  const songLoadGeneration = useRef(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const linePointerStart = useRef<LinePointerStart | null>(null);
  const lastLineTap = useRef<LineTapCandidate | null>(null);
  const lineHighlightTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-suggest: today's event with linked setlist
  const [todayEventSuggest, setTodayEventSuggest] = useState<{ eventName: string; setlistId: string; setlistName: string } | null>(null);
  const signalTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSyncTime = useRef(0);
  const pendingScrollAnchor = useRef<ScrollAnchor | null>(null);
  const initialScrollRestored = useRef(false);
  const { findAnchorAtScroll, scrollToAnchor } = useScrollAnchors();
  const sections = useMemo(() => currentSong?.latest_version?.sections ?? [], [currentSong?.latest_version?.sections]);

  // ── View Mode (TX20-TX24) ────────────────────────────────────────────────
  const myMemberData = session.members.find((m) => m.user_id === user?.id);
  const [viewMode, setViewMode] = useState<ViewMode>(
    (myMemberData?.view_mode as ViewMode | undefined) ?? 'STANDARD',
  );
  const [capoFret, setCapoFret] = useState<number>(myMemberData?.capo_fret ?? 1);
  const viewModeDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ────────────────────────────────────────────────────────────────────────

  const clearSignal = useCallback(() => {
    setActiveSignal(null);
    if (signalTimeout.current) clearTimeout(signalTimeout.current);
  }, []);

  const clearLineHighlight = useCallback(() => {
    setActiveLineHighlight(null);
    if (lineHighlightTimeout.current) clearTimeout(lineHighlightTimeout.current);
    lineHighlightTimeout.current = null;
    linePointerStart.current = null;
    lastLineTap.current = null;
  }, []);

  const showLineHighlight = useCallback((highlight: ActiveLineHighlight) => {
    if (lineHighlightTimeout.current) clearTimeout(lineHighlightTimeout.current);
    setActiveLineHighlight(highlight);
    lineHighlightTimeout.current = setTimeout(() => {
      setActiveLineHighlight((current) => current?.highlightId === highlight.highlightId ? null : current);
      lineHighlightTimeout.current = null;
    }, 2600);
  }, []);

  const showSignal = useCallback((type: string) => {
    const sig = SIGNALS[type];
    if (!sig) return;
    setActiveSignal(sig);
    const now = new Date();
    const hh = now.getHours().toString().padStart(2, '0');
    const mm = now.getMinutes().toString().padStart(2, '0');
    setAlertHistory((h) => [{ type, time: `${hh}:${mm}` }, ...h.slice(0, 9)]);
    if (signalTimeout.current) clearTimeout(signalTimeout.current);
    if (!sig.persistent) {
      signalTimeout.current = setTimeout(clearSignal, 4000);
    }
  }, [clearSignal]);

  useEffect(() => () => {
    if (signalTimeout.current) clearTimeout(signalTimeout.current);
  }, []);

  useEffect(() => {
    const generation = ++songLoadGeneration.current;
    if (!currentSongId) { setCurrentSong(null); currentSongRef.current = null; return; }
    songsApi.get(team.ministry_id, currentSongId)
      .then((r) => {
        if (generation === songLoadGeneration.current) {
          setCurrentSong(r.data);
          currentSongRef.current = r.data;
        }
      })
      .catch(() => {
        if (generation === songLoadGeneration.current) {
          setCurrentSong(null);
          currentSongRef.current = null;
        }
      });
  }, [currentSongId, team.ministry_id]);

  // Auto-suggest: check for today's event with linked setlist (leaders only, once per session)
  useEffect(() => {
    if (!isLeader || activeSetlist) return;
    const today = new Date().toISOString().slice(0, 10);
    listEvents(team.id, { date_from: today, date_to: today, status: 'SCHEDULED' })
      .then((events) => {
        const ev = events.find((e) => e.setlist_id);
        if (ev && ev.setlist_id) {
          setTodayEventSuggest({
            eventName: ev.name,
            setlistId: ev.setlist_id,
            setlistName: ev.setlist_name ?? 'Setlist do evento',
          });
        }
      })
      .catch(() => null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEvent = useCallback((event: WsEvent) => {
    switch (event.type) {
      case 'member_join':
        setMembers((m) => {
          if (m.find((x) => x.user_id === event.user_id)) {
            return m.map((x) => x.user_id === event.user_id ? { ...x, is_active: true } : x);
          }
          return [...m, { user_id: event.user_id, user_name: event.user_name, avatar_url: event.avatar_url, is_active: true }];
        });
        break;
      case 'member_leave':
        setMembers((m) => m.map((x) => x.user_id === event.user_id ? { ...x, is_active: false } : x));
        break;
      case 'song_change':
        clearLineHighlight();
        setCurrentSongId(event.song_id);
        resetRoomScroll(contentRef.current);
        setCurrentPosition(0);
        pendingScrollAnchor.current = null;
        if (event.key_override) {
          keyOverrideRef.current = event.key_override;
          setKeyOverride(event.key_override);
        } else {
          keyOverrideRef.current = '';
          setKeyOverride('');
        }
        setSetlistIndex(event.setlist_index ?? null);
        break;
      case 'setlist_load':
        {
          clearLineHighlight();
          const selection = getSetlistInitialSelection(event.setlist.items);
          const songId = event.song_id !== undefined ? event.song_id : selection.currentSongId;
          const setlistIndex = event.setlist_index !== undefined ? event.setlist_index : selection.setlistIndex;
          const keyOverride = event.key_override !== undefined ? event.key_override : selection.keyOverride;
          setActiveSetlist(event.setlist);
          setCurrentSongId(songId);
          resetRoomScroll(contentRef.current);
          setCurrentPosition(0);
          setIsPaused(selection.isPaused);
          pendingScrollAnchor.current = null;
          setSetlistIndex(setlistIndex);
          keyOverrideRef.current = keyOverride;
          setKeyOverride(keyOverride);
        }
        break;
      case 'scroll_sync':
        if (!isLeader && contentRef.current) {
          setCurrentPosition(event.position);
          if (event.anchor) {
            pendingScrollAnchor.current = event.anchor;
            scrollToAnchor(contentRef.current, event.anchor, false);
          } else {
            contentRef.current.scrollTop = event.position;
          }
        }
        break;
      case 'line_highlight':
        if (event.song_id === currentSongId) {
          showLineHighlight({
            highlightId: event.highlight_id,
            sectionId: event.section_id,
            lineIndex: event.line_index,
          });
        }
        break;
      case 'font_size_sync':
        // Sync font size from leader
        if (!isLeader) {
          useFontSizeStore.setState({ size: event.size });
        }
        break;
      case 'control':
        if (event.control === 'PAUSE')      { setIsPaused(true);  showSignal('pause'); }
        else if (event.control === 'RESUME') { setIsPaused(false); clearSignal(); }
        else if (event.control === 'TEMPO_CHANGE') {
          const bpm = event.payload?.bpm;
          if (typeof bpm === 'number') setBpmOverride(bpm);
        }
        else if (event.control === 'DRUMS_ONLY') showSignal('drums');
        else if (event.control === 'TRANSPOSE') {
          const newKey = event.payload?.key as string | undefined;
          if (newKey) {
            const oldKey = keyOverrideRef.current || currentSongRef.current?.key || '';
            const direction = oldKey ? semitonesFromTo(oldKey, newKey) : 1;
            keyOverrideRef.current = newKey;
            setKeyOverride(newKey);
            showSignal(direction >= 0 ? 'key-up' : 'key-down');
          } else {
            showSignal('key-up');
          }
        }
        else if (event.control === 'CHANGE')     showSignal('change');
        else if (event.control === 'SLOW')       showSignal('slow');
        break;
      case 'session_end':
        isEndingSession.current = true; // suppress WS close error for all clients
        onEnd();
        break;
    }
  }, [isLeader, currentSongId, scrollToAnchor, showSignal, clearSignal, clearLineHighlight, showLineHighlight, onEnd, isEndingSession]);

  const onSessionState = useCallback(async (snapshot: SessionSnapshot) => {
    const recovered = mapSessionSnapshot(snapshot);
    const generation = ++songLoadGeneration.current;
    let recoveredSong: Song | null = null;
    if (recovered.currentSongId) {
      try {
        const response = await songsApi.get(team.ministry_id, recovered.currentSongId);
        recoveredSong = response.data;
      } catch {
        recoveredSong = null;
      }
    }
    if (generation !== songLoadGeneration.current) return;

    setCurrentSongId(recovered.currentSongId);
    clearLineHighlight();
    setCurrentSong(recoveredSong);
    currentSongRef.current = recoveredSong;
    setActiveSetlist(recovered.activeSetlist);
    setSetlistIndex(recovered.setlistIndex);
    setIsPaused(recovered.isPaused);
    setKeyOverride(recovered.keyOverride);
    keyOverrideRef.current = recovered.keyOverride;
    setBpmOverride(recovered.bpmOverride);
    setCurrentPosition(recovered.currentPosition);
    pendingScrollAnchor.current = recovered.scrollAnchor;
    initialScrollRestored.current = false;
    setMembers(recovered.members);
    setViewMode(recovered.viewMode);
    setCapoFret(recovered.capoFret);
    useFontSizeStore.getState().setSize(recovered.leaderFontSize);
    if (snapshot.session.status === 'ENDED') {
      isEndingSession.current = true;
      onEnd();
    }
  }, [team.ministry_id, clearLineHighlight, onEnd, isEndingSession]);

  const {
    sendSongChange,
    sendControl,
    sendScroll,
    sendFontSize,
    sendLineHighlight,
    sendViewMode,
    sendEnd,
    sendSetlistLoad,
    isSynchronized,
  } = useSessionWS({
    sessionId: session.id,
    teamId: team.id,
    onEvent,
    onSessionState,
    onOpen: () => {
      console.log('✅ WebSocket connection established');
      onWsError(null);
    },
    onConnectionState: setConnectionState,
    onTerminalError: (code) => {
      if (code === 'SESSION_ENDED') {
        isEndingSession.current = true;
        onEnd();
      } else {
        onWsError('Não foi possível recuperar o acesso à sessão. Faça login novamente ou peça acesso à equipe.');
      }
    },
  });

  const handleLinePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>, target: SongLineTarget) => {
    if (!isLeader || !isSynchronized || !event.isPrimary || event.button !== 0) return;
    linePointerStart.current = {
      ...target,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  }, [isLeader, isSynchronized]);

  const handleLinePointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>, target: SongLineTarget) => {
    const start = linePointerStart.current;
    linePointerStart.current = null;
    if (!start || start.pointerId !== event.pointerId || start.sectionId !== target.sectionId || start.lineIndex !== target.lineIndex) return;
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 24) {
      lastLineTap.current = null;
      return;
    }

    const now = Date.now();
    const previousTap = lastLineTap.current;
    const currentTap = { ...target, x: event.clientX, y: event.clientY, time: now };
    if (isLineDoubleTap(previousTap, currentTap)) {
      lastLineTap.current = null;
      const highlightId = `${now}-${Math.random().toString(36).slice(2)}`;
      const highlight = { highlightId, ...target };
      showLineHighlight(highlight);
      if (currentSongId) sendLineHighlight(highlightId, currentSongId, target.sectionId, target.lineIndex);
      return;
    }

    lastLineTap.current = currentTap;
  }, [currentSongId, sendLineHighlight, showLineHighlight]);

  const handleLinePointerCancel = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (linePointerStart.current?.pointerId === event.pointerId) linePointerStart.current = null;
    lastLineTap.current = null;
  }, []);

  useEffect(() => () => {
    if (lineHighlightTimeout.current) clearTimeout(lineHighlightTimeout.current);
  }, []);

  // TX22: send view_mode_change (debounced 300 ms) whenever viewMode/capoFret changes
  useEffect(() => {
    if (viewModeDebounce.current) clearTimeout(viewModeDebounce.current);
    viewModeDebounce.current = setTimeout(() => {
      if (isSynchronized) sendViewMode(viewMode, viewMode === 'CAPO' ? capoFret : null);
    }, 300);
    return () => {
      if (viewModeDebounce.current) clearTimeout(viewModeDebounce.current);
    };
  }, [viewMode, capoFret, isSynchronized, sendViewMode]);

  // Scroll sync: leader's scroll position is broadcast to all followers in real-time
  const handleLeaderScroll = useCallback(() => {
    if (!isLeader || !isSynchronized) return;
    const now = performance.now();
    if (now - lastSyncTime.current < 50) return; // throttle to 20 updates/s for better UX
    lastSyncTime.current = now;
    const el = contentRef.current;
    if (!el) return;

    sendScroll(el.scrollTop, findAnchorAtScroll(el));
  }, [findAnchorAtScroll, isLeader, isSynchronized, sendScroll]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container || !pendingScrollAnchor.current || sections.length === 0) return;
    const anchor = pendingScrollAnchor.current;
    const anchorRestored = scrollToAnchor(container, anchor, false);
    if (!anchorRestored) {
      const fallback = getScrollRestoreTarget(anchor, currentPosition, false);
      container.scrollTop = fallback?.type === 'position' ? fallback.position : 0;
    }
    pendingScrollAnchor.current = null;
    initialScrollRestored.current = true;
  }, [scrollToAnchor, sections, currentPosition]);

  useEffect(() => {
    const container = contentRef.current;
    if (initialScrollRestored.current || !container || sections.length === 0 || pendingScrollAnchor.current) return;

    initialScrollRestored.current = true;
    const target = getScrollRestoreTarget(null, currentPosition);
    if (target?.type === 'position') container.scrollTop = target.position;
  }, [sections.length, currentPosition]);

  // Font size sync: leader's font size is broadcast to followers
  // Wrapper for increaseFontSize that syncs to followers if leader
  const handleIncreaseFontSize = useCallback(() => {
    if (!isSynchronized) return;
    const newSize = fontSize === 'sm' ? 'md' : fontSize === 'md' ? 'lg' : fontSize === 'lg' ? 'xl' : fontSize === 'xl' ? '2xl' : '2xl';
    increaseFontSize();
    if (isLeader) sendFontSize(newSize);
  }, [increaseFontSize, fontSize, isLeader, isSynchronized, sendFontSize]);

  // Wrapper for decreaseFontSize that syncs to followers if leader
  const handleDecreaseFontSize = useCallback(() => {
    if (!isSynchronized) return;
    const newSize = fontSize === '2xl' ? 'xl' : fontSize === 'xl' ? 'lg' : fontSize === 'lg' ? 'md' : fontSize === 'md' ? 'sm' : 'sm';
    decreaseFontSize();
    if (isLeader) sendFontSize(newSize);
  }, [decreaseFontSize, fontSize, isLeader, isSynchronized, sendFontSize]);

  const handleSongSelect = (songId: string) => {
    if (!isLeader || !isSynchronized) return;
    setCurrentSongId(songId);
    resetRoomScroll(contentRef.current);
    setSetlistIndex(null);
    keyOverrideRef.current = '';
    setKeyOverride('');
    sendSongChange(songId);
    setSidebarOpen(false);
  };

  const handleSongSelectFromSetlist = (songId: string, index: number) => {
    if (!isLeader || !isSynchronized) return;
    setCurrentSongId(songId);
    resetRoomScroll(contentRef.current);
    setSetlistIndex(index);
    keyOverrideRef.current = '';
    setKeyOverride('');
    sendSongChange(songId, index);
    setSidebarOpen(false);
  };

  const handleLoadSetlist = (setlistId: string) => {
    if (!isLeader || !isSynchronized) return;
    sendSetlistLoad(setlistId);
  };

  const togglePause = () => {
    if (!isLeader || !isSynchronized) return;
    const next = !isPaused;
    setIsPaused(next);
    sendControl(next ? 'PAUSE' : 'RESUME');
    if (next) showSignal('pause'); else clearSignal();
  };

  const handleSignal = (type: string, control: ControlEventType | null) => {
    if (!isLeader || !isSynchronized) return;
    showSignal(type);
    if (control) sendControl(control);
  };

  const handleTranspose = useCallback((direction: 1 | -1) => {
    if (!isLeader || !isSynchronized) return;
    const currentKey = keyOverrideRef.current || currentSongRef.current?.key || 'C';
    const newKey = transposeKey(currentKey, direction);
    keyOverrideRef.current = newKey;
    setKeyOverride(newKey);
    sendControl('TRANSPOSE', { key: newKey });
  }, [isLeader, isSynchronized, sendControl]);

  const handleEndSession = () => {
    if (!isLeader || !isSynchronized) return;
    isEndingSession.current = true;
    sendEnd();
    onEnd(); // Navigate away first, then clean up server-side
    sessionsApi.end(team.id, session.id).catch(() => null);
  };

  const semitones = (currentSong && keyOverride) ? semitonesFromTo(currentSong.key, keyOverride) : 0;
  const activeKey = keyOverride || currentSong?.key || '';

  // TX20b / TX21: capo incompatibility banner data
  const activeKeySemitone = activeKey
    ? 'CDEFGAB'.indexOf(activeKey[0]) !== -1
      ? [0, 2, 4, 5, 7, 9, 11]['CDEFGAB'.indexOf(activeKey[0])] +
        (activeKey[1] === '#' ? 1 : activeKey[1] === 'b' ? -1 : 0)
      : -1
    : -1;
  const capoIncompatible =
    viewMode === 'CAPO' && activeKeySemitone >= 0 && !isCapoCompatible(activeKeySemitone, capoFret);
  const capoSuggestions = capoIncompatible
    ? getCapoSuggestions(activeKeySemitone, capoFret)
    : [];

  return (
    <div className="flex-1 flex flex-col lg:flex-row min-h-0" style={{ backgroundColor: theme.colors.bg.primary, color: theme.colors.text.primary }}>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Panel */}
      <div
        className={`
        fixed inset-y-0 left-0 z-30 w-72 flex flex-col transition-transform duration-300 border-r
        lg:static lg:w-80 lg:translate-x-0 lg:z-auto
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
        style={{
          backgroundColor: theme.mode === 'dark' ? 'rgba(30, 30, 40, 0.95)' : 'rgba(245, 245, 250, 0.98)',
          borderRightColor: theme.colors.border,
        }}
      >
      <div className="border-b px-5 py-5" style={{ borderBottomColor: theme.colors.border }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: theme.colors.text.tertiary }}>Sessão ao vivo</p>
              <h2 className="font-semibold text-sm" style={{ color: theme.colors.text.primary, fontFamily: "'Playfair Display', serif" }}>
                {team.name}
              </h2>
            </div>
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{
              backgroundColor: `${theme.colors.accent}20`,
              color: theme.colors.accent,
            }}>
              {isLeader ? '★ Líder' : 'Seguidor'}
            </span>
            {isLeader && (
              <button
                onClick={handleEndSession}
                disabled={!isSynchronized}
                className="text-xs cursor-pointer transition-colors"
                style={{ color: '#f87171' }}
              >
                Encerrar
              </button>
            )}
          </div>
        </div>

        {/* Members */}
        <div className="px-5 py-4 border-b" style={{ borderBottomColor: theme.colors.border }}>
          <p className="text-xs uppercase tracking-widest mb-3" style={{ color: theme.colors.text.tertiary }}>
            Membros ({members.filter((m) => m.is_active).length}/{members.length})
          </p>
          <div className="flex flex-col gap-2">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-3">
                <div className="relative">
                  <UserAvatar avatarUrl={m.avatar_url || null} name={m.user_name} size="sm" />
                  <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 ${m.is_active ? 'bg-green-400' : ''}`} style={{
                    borderColor: theme.colors.bg.secondary,
                    backgroundColor: !m.is_active ? theme.colors.text.tertiary : undefined,
                  }}></span>
                </div>
                <p className="text-xs font-medium truncate" style={{ color: theme.colors.text.primary }}>
                  {m.user_name}
                  {m.user_id === session.leader_id && <span style={{ color: theme.colors.accent }} className="ml-1">★</span>}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Song list / Setlist */}
        <div className="flex-1 overflow-y-auto flex flex-col">
          {isLeader && isSynchronized && (
            <div className="border-b pt-3" style={{ borderBottomColor: theme.colors.border }}>
              <LoadSetlistPicker
                teamId={team.id}
                currentSetlistName={activeSetlist?.name}
                onLoad={handleLoadSetlist}
                theme={theme}
              />
            </div>
          )}

          {activeSetlist ? (
            <SetlistSidebar
              setlist={activeSetlist}
              currentSongId={currentSongId}
              currentIndex={setlistIndex}
              isLeader={isLeader && isSynchronized}
              onSelectSong={handleSongSelectFromSetlist}
              theme={theme}
            />
          ) : (
            <div className="px-5 py-4">
              <p className="text-xs uppercase tracking-widest mb-3" style={{ color: theme.colors.text.tertiary }}>Músicas</p>
              {songs.length === 0 && (
                <p className="text-xs" style={{ color: theme.colors.text.tertiary }}>Nenhuma música na biblioteca.</p>
              )}
              <div className="flex flex-col gap-2">
                {songs.map((song, i) => (
                  <button
                    key={song.id}
                    onClick={() => isLeader && handleSongSelect(song.id)}
                    disabled={!isLeader || !isSynchronized}
                    className="flex items-center gap-3 p-3 rounded-xl text-left transition-colors"
                    style={{
                      backgroundColor: currentSongId === song.id ? `${theme.colors.accent}10` : 'transparent',
                      borderColor: currentSongId === song.id ? `${theme.colors.accent}20` : 'transparent',
                      cursor: isLeader && isSynchronized ? 'pointer' : 'default',
                      opacity: isLeader && isSynchronized || currentSongId === song.id ? 1 : 0.6,
                    }}
                  >
                    <span className="text-xs w-4" style={{ color: theme.colors.text.tertiary }}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{
                        color: currentSongId === song.id ? theme.colors.accent : theme.colors.text.secondary
                      }}>
                        {song.title}
                      </p>
                      <p className="text-xs truncate" style={{ color: theme.colors.text.tertiary }}>{song.artist}</p>
                    </div>
                    {song.key && <span className="text-xs" style={{ color: theme.colors.text.tertiary }}>{formatSongKey(song.key, song.mode)}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Alert history */}
        {alertHistory.length > 0 && (
          <div className="px-5 py-4 border-t" style={{ borderTopColor: theme.colors.border }}>
            <p className="text-xs uppercase tracking-widest mb-3" style={{ color: theme.colors.text.tertiary }}>Histórico</p>
            <div className="flex flex-col gap-2 max-h-32 overflow-y-auto">
              {alertHistory.map((a, i) => {
                const sig = SIGNALS[a.type];
                return (
                  <div key={i} className="flex items-center gap-2">
                    <i className={`${sig?.icon} text-xs`} style={{ color: sig?.color }}></i>
                    <span className="text-xs flex-1" style={{ color: theme.colors.text.tertiary }}>{sig?.label}</span>
                    <span className="text-xs" style={{ color: theme.colors.text.tertiary }}>{a.time}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Main stage */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <CollabConnectionStatus state={connectionState} />
        {/* SessionBanner — fixed at top, persistent signals show dismiss button */}
        {activeSignal && (
          <SessionBanner signal={activeSignal} onDismiss={clearSignal} />
        )}

        {/* Phase D: auto-suggest today's event setlist */}
        {todayEventSuggest && isLeader && isSynchronized && !activeSetlist && (
          <div className="flex items-center gap-3 px-4 py-2 text-xs border-b border-[#C8A96E]/30 bg-[#C8A96E]/10">
            <i className="ri-calendar-event-line text-[#C8A96E] flex-shrink-0"></i>
            <span className="text-white/70 flex-1 min-w-0 truncate">
              Evento de hoje: <span className="text-white font-medium">{todayEventSuggest.eventName}</span>
              {' '}— Carregar setlist <span className="text-[#C8A96E]">"{todayEventSuggest.setlistName}"</span>?
            </span>
            <button
              onClick={() => { handleLoadSetlist(todayEventSuggest.setlistId); setTodayEventSuggest(null); }}
              className="text-[#C8A96E] hover:text-[#D4B87A] font-medium flex-shrink-0"
            >
              Carregar
            </button>
            <button
              onClick={() => setTodayEventSuggest(null)}
              className="text-white/30 hover:text-white/60 flex-shrink-0"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {/* TX20b: Capo incompatibility banner */}
        {capoIncompatible && (
          <div
            role="alert"
            className="flex items-center gap-2 px-4 py-2 text-xs border-b"
            style={{ backgroundColor: '#78350f20', borderColor: '#d97706', color: '#d97706' }}
          >
            <i className="ri-error-warning-line flex-shrink-0"></i>
            <span>
              Capotraste {capoFret}ª casa não é compatível com tom {formatSongKey(activeKey, currentSong?.mode)}.
              {capoSuggestions.length > 0 && (
                <> Sugestões:{' '}
                  {capoSuggestions.map((s, i) => (
                    <button
                      key={s.fret}
                      className="underline cursor-pointer hover:opacity-70 mx-1"
                      onClick={() => isSynchronized && setCapoFret(s.fret)}
                      disabled={!isSynchronized}
                    >
                      {s.fret === 0 ? 'sem capo' : `${s.fret}ª (${s.shape})`}
                      {i < capoSuggestions.length - 1 ? ',' : ''}
                    </button>
                  ))}
                </>
              )}
            </span>
          </div>
        )}

        {/* Song header */}
        <div className="flex items-center justify-between px-4 lg:px-8 py-3 lg:py-5 border-b gap-2" style={{ borderBottomColor: theme.colors.border, backgroundColor: theme.colors.bg.secondary }}>
          <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-8 h-8 flex items-center justify-center transition-colors cursor-pointer flex-shrink-0"
              style={{ color: theme.colors.text.secondary }}
            >
              <i className="ri-menu-line text-lg"></i>
            </button>
            <div className="min-w-0 flex-1">
              {currentSong ? (
                <>
                  <h2 className="text-base lg:text-xl font-semibold truncate" style={{ color: theme.colors.text.primary, fontFamily: "'Playfair Display', serif" }}>
                    {currentSong.title}
                  </h2>
                  <p style={{ color: theme.colors.text.secondary }} className="text-xs lg:text-sm truncate">{currentSong.artist}</p>
                </>
              ) : (
                <p style={{ color: theme.colors.text.tertiary }} className="text-xs lg:text-sm">{isLeader ? 'Selecione uma música' : 'Aguardando o líder...'}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 lg:gap-3 flex-shrink-0">
            {currentSong && activeKey && (
              isLeader ? (
                <div
                  className="flex items-center rounded-lg border"
                  style={{ backgroundColor: `${theme.colors.accent}10`, borderColor: `${theme.colors.accent}30` }}
                >
                  <button
                    onClick={() => handleTranspose(-1)}
                    disabled={!isSynchronized}
                    className="w-7 h-7 flex items-center justify-center rounded-l-lg transition-colors cursor-pointer hover:opacity-70"
                    title="Baixar tom"
                    style={{ color: theme.colors.accent }}
                  >
                    <i className="ri-subtract-line text-sm"></i>
                  </button>
                  <span className="text-xs lg:text-sm font-bold px-2 min-w-[2.5rem] text-center" style={{ color: theme.colors.accent }}>
                    {formatSongKey(activeKey, currentSong.mode)}
                  </span>
                  <button
                    onClick={() => handleTranspose(1)}
                    disabled={!isSynchronized}
                    className="w-7 h-7 flex items-center justify-center rounded-r-lg transition-colors cursor-pointer hover:opacity-70"
                    title="Subir tom"
                    style={{ color: theme.colors.accent }}
                  >
                    <i className="ri-add-line text-sm"></i>
                  </button>
                </div>
              ) : (
                <span className="text-xs lg:text-sm font-bold px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg" style={{
                  backgroundColor: `${theme.colors.accent}10`,
                  color: theme.colors.accent,
                }}>
                  {formatSongKey(activeKey, currentSong.mode)}
                </span>
              )
            )}
            {(bpmOverride ?? currentSong?.bpm) && (
              <span style={{ color: theme.colors.text.tertiary }} className="text-xs lg:text-sm hidden lg:inline">{bpmOverride ?? currentSong?.bpm} BPM</span>
            )}
            {/* TX21: Capo fret badge */}
            {viewMode === 'CAPO' && (
              <span
                className="text-xs font-semibold px-2 py-1 rounded-lg hidden lg:inline"
                style={{ backgroundColor: '#d9770620', color: '#d97706' }}
              >
                Capo {capoFret}ª
              </span>
            )}
          </div>
        </div>

        {/* Scrollable content — leader scroll is synced to followers */}
        <div
          ref={contentRef}
          onScroll={handleLeaderScroll}
          className="flex-1 overflow-y-auto overflow-x-hidden px-3 lg:px-8 py-8 pb-32 lg:pb-8"
          style={{ backgroundColor: sheetPalette.background }}
        >
          {sections.length > 0 ? (
            <div className="max-w-2xl lg:max-w-3xl mx-auto w-full">
              <SongContent
                sections={sections}
                sheetPalette={sheetPalette}
                fontSize={fontSizeClass}
                semitones={semitones}
                viewMode={viewMode}
                capoFret={capoFret}
                activeHighlight={activeLineHighlight}
                onLinePointerDown={isLeader && isSynchronized ? handleLinePointerDown : undefined}
                onLinePointerUp={isLeader && isSynchronized ? handleLinePointerUp : undefined}
                onLinePointerCancel={isLeader && isSynchronized ? handleLinePointerCancel : undefined}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p style={{ color: sheetPalette.muted }} className="text-sm">
                {isLeader ? 'Selecione uma música para começar.' : 'Aguardando o líder selecionar uma música...'}
              </p>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="border-t px-3 lg:px-6 py-1.5" style={{ borderTopColor: theme.colors.border, backgroundColor: theme.colors.bg.secondary }}>
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-none">

            {/* Play / Pause */}
            <button
              onClick={togglePause}
              disabled={!isLeader || !isSynchronized}
              className="w-9 h-9 flex items-center justify-center rounded-full transition-all flex-shrink-0"
              style={{
                backgroundColor: isPaused ? theme.colors.bg.primary : theme.colors.accent,
                color: isPaused ? theme.colors.text.primary : theme.colors.bg.primary,
                cursor: isLeader ? 'pointer' : 'default',
                opacity: isLeader ? 1 : 0.3,
              }}
            >
              <i className={`${isPaused ? 'ri-play-fill' : 'ri-pause-fill'} text-base`}></i>
            </button>

            {/* Divider */}
            <div className="w-px h-5 flex-shrink-0" style={{ backgroundColor: theme.colors.border }}></div>

            {/* View mode */}
            <div className="flex items-center rounded-full p-0.5 border gap-0.5 flex-shrink-0" style={{ borderColor: theme.colors.border }}>
              {(['STANDARD', 'BASS', 'CAPO'] as ViewMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => isSynchronized && setViewMode(m)}
                  disabled={!isSynchronized}
                  className="px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
                  style={{
                    backgroundColor: viewMode === m ? theme.colors.accent : 'transparent',
                    color: viewMode === m ? theme.colors.bg.primary : theme.colors.text.secondary,
                  }}
                >
                  {m === 'STANDARD' ? 'Padrão' : m === 'BASS' ? 'Baixo' : 'Capo'}
                </button>
              ))}
            </div>

            {/* Capo fret — always rendered to keep footer height stable; hidden when not in CAPO mode */}
            <div
              className="flex items-center rounded-lg border flex-shrink-0"
              style={{
                borderColor: '#d97706',
                backgroundColor: '#d9770610',
                visibility: viewMode === 'CAPO' ? 'visible' : 'hidden',
                pointerEvents: viewMode === 'CAPO' ? 'auto' : 'none',
              }}
            >
              <button
                onClick={() => setCapoFret((f) => Math.max(1, f - 1))}
                disabled={!isSynchronized}
                className="w-6 h-6 flex items-center justify-center transition-colors cursor-pointer hover:opacity-70"
                style={{ color: '#d97706' }}
                aria-label="Diminuir casa do capo"
              >
                <i className="ri-subtract-line text-xs"></i>
              </button>
              <span className="text-xs font-bold px-1 min-w-[1.75rem] text-center" style={{ color: '#d97706' }}>
                {capoFret}ª
              </span>
              <button
                onClick={() => setCapoFret((f) => Math.min(12, f + 1))}
                disabled={!isSynchronized}
                className="w-6 h-6 flex items-center justify-center transition-colors cursor-pointer hover:opacity-70"
                style={{ color: '#d97706' }}
                aria-label="Aumentar casa do capo"
              >
                <i className="ri-add-line text-xs"></i>
              </button>
            </div>

            {/* Divider */}
            <div className="w-px h-5 flex-shrink-0" style={{ backgroundColor: theme.colors.border }}></div>

            {/* Font size */}
            <div className="flex items-center gap-0.5 rounded-full p-0.5 border flex-shrink-0" style={{ borderColor: theme.colors.border }}>
              <button
                onClick={handleDecreaseFontSize}
                disabled={!isSynchronized}
                className="w-6 h-6 flex items-center justify-center rounded-full transition-colors"
                style={{ color: theme.colors.text.secondary }}
                title="Diminuir fonte"
              >
                <i className="ri-subtract-line text-xs"></i>
              </button>
              <span className="text-xs px-1.5 min-w-[1.5rem] text-center" style={{ color: theme.colors.text.secondary }}>
                {fontSize === 'sm' ? 'S' : fontSize === 'md' ? 'M' : fontSize === 'lg' ? 'L' : fontSize === 'xl' ? 'XL' : '2XL'}
              </span>
              <button
                onClick={handleIncreaseFontSize}
                disabled={!isSynchronized}
                className="w-6 h-6 flex items-center justify-center rounded-full transition-colors"
                style={{ color: theme.colors.text.secondary }}
                title="Aumentar fonte"
              >
                <i className="ri-add-line text-xs"></i>
              </button>
            </div>

            {/* Divider + Leader signals */}
            {isLeader && (
              <>
                <div className="w-px h-5 flex-shrink-0" style={{ backgroundColor: theme.colors.border }}></div>
                {(
                  [
                    { type: 'change', label: 'Mudança', icon: 'ri-arrow-right-circle-line', control: 'CHANGE'     },
                    { type: 'drums',  label: 'Bateria',  icon: 'ri-rhythm-line',             control: 'DRUMS_ONLY' },
                    { type: 'slow',   label: 'Ralentar', icon: 'ri-speed-line',              control: 'SLOW'       },
                  ] as { type: string; label: string; icon: string; control: ControlEventType }[]
                ).map((btn) => (
                  <button
                    key={btn.type}
                    onClick={() => handleSignal(btn.type, btn.control)}
                    disabled={!isSynchronized}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all whitespace-nowrap border flex-shrink-0"
                    title={btn.label}
                    style={{
                      backgroundColor: activeSignal?.type === btn.type ? theme.colors.accent : theme.colors.bg.primary,
                      color: activeSignal?.type === btn.type ? theme.colors.bg.primary : theme.colors.text.secondary,
                      borderColor: activeSignal?.type === btn.type ? theme.colors.accent : theme.colors.border,
                    }}
                  >
                    <i className={`${btn.icon} text-sm`}></i>
                    <span>{btn.label}</span>
                  </button>
                ))}
              </>
            )}

            {/* Follower status */}
            {!isLeader && (
              <span className="text-xs flex-shrink-0" style={{ color: theme.colors.text.tertiary }}>
                <i className="ri-user-follow-line mr-1"></i>Seguindo o líder
              </span>
            )}
          </div>
        </div>
        {/* Safe area spacer — provides consistent bottom breathing room; extra height on iPhone for home indicator */}
        <div style={{ height: 'max(0.5rem, env(safe-area-inset-bottom))', backgroundColor: theme.colors.bg.secondary }} />
      </div>
    </div>
  );
};

// ── Page root ─────────────────────────────────────────────────────────────────

const CollabPage = () => {
  // Protect route - redirect to login if not authenticated
  useAuthGuard();

  const theme = DARK_THEME;
  const { invalidate: invalidateSubscriptionStore } = useSubscriptionStore();
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamContexts, setTeamContexts] = useState<TeamContext[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [songs, setSongs] = useState<SongListItem[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wsConnectionError, setWsConnectionError] = useState<string | null>(null);
  const isEndingSessionRef = useRef(false);

  // Controle de acesso: plano pago ou trial ativo
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasPersonalCollabAccess, setHasPersonalCollabAccess] = useState(false);
  const [trialUsed, setTrialUsed] = useState(true);
  const hasCollabAccess =
    hasPersonalCollabAccess || teamHasCollabAccess(selectedTeamId, teamContexts);

  useEffect(() => {
    Promise.all([
      subscriptionApi.getCurrentSubscription().catch(() => null),
      subscriptionApi.getTrialStatus().catch(() => null),
      teamsApi.getContexts().catch(() => null),
    ]).then(([sub, trial, teamContextsResponse]) => {
      const hasPaidPlan = sub?.status === 'active' && sub?.plan_detail?.code !== 'free';
      const trialActive = trial?.trial_active ?? false;
      
      setHasPersonalCollabAccess(hasPaidPlan || trialActive);
      setTeamContexts(teamContextsResponse?.data ?? []);
      setTrialUsed(trial?.trial_used ?? true);
      setAccessChecked(true);
    });
  }, []);

  // Load teams on mount
  useEffect(() => {
    teamsApi.list()
      .then((res) => {
        console.log('=== COLLAB PAGE ===');
        console.log('Equipes carregadas:', res.data.length);
        console.log('Times:', res.data.map((t) => ({ id: t.id, name: t.name })));
        setTeams(res.data);
        
        const teamToSelect = getDefaultCollabTeamId(res.data);
        if (teamToSelect) {
          setSelectedTeamId(teamToSelect);
        } else {
            // No teams available - try to create a default one
            console.log('Nenhuma equipe encontrada. Criando equipe padrão...');
            teamsApi.create('Minha Banda', useMinistryStore.getState().activeMinistryId ?? '')
              .then((createRes) => {
                console.log('Equipe padrão criada:', {
                  id: createRes.data.id,
                  name: createRes.data.name,
                });
                setTeams([createRes.data]); // ← First team, so reset array
                setSelectedTeamId(createRes.data.id);
              })
              .catch((err) => {
                console.error('Erro ao criar equipe padrão:', err);
                setError('Nenhuma equipe disponível. Crie uma equipe para começar.');
              });
            return;
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar equipes:', err);
        setError('Erro ao carregar equipes.');
      })
      .finally(() => setLoading(false));
  }, []);

  // Load songs and sessions when team changes
  useEffect(() => {
    if (!selectedTeamId || !accessChecked) {
      console.log('⏭ Nenhum time selecionado, skipping load');
      return;
    }

    if (!hasCollabAccess) {
      setSongs([]);
      setSessions([]);
      setError('');
      setLoading(false);
      return;
    }

    const selectedTeam = teams.find((t) => t.id === selectedTeamId);
    const ministryId = selectedTeam?.ministry_id;

    if (!ministryId) {
      console.log('⏭ Ministry ID não disponível ainda, aguardando teams...');
      return;
    }

    console.log('⏳ Iniciando load de dados para time:', selectedTeamId, 'ministry:', ministryId);
    setLoading(true);
    setError('');
    
    // Load both songs and sessions
    Promise.all([
      songsApi.list(ministryId, { limit: 50 }),
      sessionsApi.list(selectedTeamId),
    ])
      .then(([songsRes, sessionsRes]) => {
        console.log('📡 Resposta da API (Músicas):', {
          status: songsRes.status,
          statusText: songsRes.statusText,
          data: songsRes.data,
        });
        console.log('📡 Resposta da API (Sessões):', {
          status: sessionsRes.status,
          statusText: sessionsRes.statusText,
          data: sessionsRes.data,
        });
        
        console.log('✅ Dados carregados:');
        console.log('   Músicas:', songsRes.data.results.length);
        console.log('   Sessões:', sessionsRes.data.length);
        if (songsRes.data.results.length === 0) {
          console.warn('⚠️ Nenhuma música encontrada para este time!');
        } else {
          console.log('   Nomes das músicas:', songsRes.data.results.map((s) => s.title));
        }
        setSongs(songsRes.data.results);
        setSessions(sessionsRes.data);
        setError('');
      })
      .catch((err) => {
        console.error('❌ Erro ao carregar dados da equipe:', {
          error: err,
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
        });
        setError('Erro ao carregar dados da equipe. Verifique sua conexão.');
      })
      .finally(() => setLoading(false));
  }, [accessChecked, hasCollabAccess, selectedTeamId, teams]);

  // Update team object when teams or selectedTeamId changes
  useEffect(() => {
    if (!selectedTeamId || teams.length === 0) {
      setTeam(null);
      return;
    }
    const selectedTeam = teams.find((t) => t.id === selectedTeamId);
    if (selectedTeam) {
      console.log('📋 Time selecionado:', { id: selectedTeam.id, name: selectedTeam.name });
      setTeam(selectedTeam);
    }
  }, [selectedTeamId, teams]);

  const handleSelectTeam = (teamId: string) => {
    setSelectedTeamId(teamId);
  };

  const handleCreate = async () => {
    if (!team) return;
    try {
      console.log('🎬 Criando nova sessão...');
      console.log('   Team ID:', team.id);
      console.log('   Access token:', localStorage.getItem('access_token') ? '✓' : '✗');
      const res = await sessionsApi.create(team.id);
      console.log('✅ Sessão criada:', res.data.id);
      setActiveSession(res.data);
      
      // Ensure songs are loaded - reload if needed
      if (songs.length === 0) {
        console.log('⚠️ Nenhuma música carregada. Recarregando...');
        songsApi.list(team.ministry_id, { limit: 50 })
          .then((songsRes) => {
            console.log('✅ Músicas recarregadas:', songsRes.data.results.length);
            setSongs(songsRes.data.results);
          })
          .catch((err) => console.error('❌ Erro ao recarregar músicas:', err));
      }
    } catch (err) {
      console.error('❌ Erro ao criar sessão:', err);
      setError('Erro ao criar sessão.');
    }
  };

  const handleJoin = (s: Session) => {
    console.log('🚪 Entrando em sessão:', s.id);
    setActiveSession(s);

    // Ensure songs are loaded - reload if needed
    if (songs.length === 0 && team) {
      console.log('⚠️ Nenhuma música carregada. Recarregando...');
      songsApi.list(team.ministry_id, { limit: 50 })
        .then((songsRes) => {
          console.log('✅ Músicas recarregadas:', songsRes.data.results.length);
          setSongs(songsRes.data.results);
        })
        .catch((err) => console.error('❌ Erro ao recarregar músicas:', err));
    }
  };

  const handleEnd = () => {
    setActiveSession(null);
    setWsConnectionError(null);
  };

  const handleTrialStarted = () => {
    setHasPersonalCollabAccess(true);
    setTrialUsed(true);
    invalidateSubscriptionStore(); // so Navbar badge updates to GRP
  };

  return (
    <div className="flex flex-col" style={{ height: '100dvh', backgroundColor: theme.colors.bg.primary, color: theme.colors.text.primary }}>
      <Navbar />
      <div className="flex-1 pt-16 flex flex-col min-h-0">
        {!accessChecked ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full border-2 animate-spin" style={{
              borderColor: `${theme.colors.accent}30`,
              borderTopColor: theme.colors.accent,
            }}></div>
          </div>
        ) : loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full border-2" style={{
              borderColor: `${theme.colors.accent}30`,
              borderTopColor: theme.colors.accent,
            }}></div>
          </div>
        ) : !team ? (
          <TeamSelector
            teams={teams}
            selectedTeamId={selectedTeamId}
            onSelectTeam={handleSelectTeam}
            theme={theme}
          />
        ) : !hasCollabAccess ? (
          <CollabUpgradeGate
            trialUsed={trialUsed}
            onTrialStarted={handleTrialStarted}
            onChangeTeam={() => {
              setTeam(null);
              setSelectedTeamId(null);
            }}
          />
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        ) : activeSession && team ? (
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            {wsConnectionError && (
              <div className="bg-red-900/30 border border-red-500/50 p-4 text-red-400 text-sm">
                <div className="flex items-start gap-2">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <div>
                    <p className="font-semibold">Conexão perdida</p>
                    <p>{wsConnectionError}</p>
                  </div>
                </div>
              </div>
            )}
            <CollabRoom
              session={activeSession}
              team={team}
              songs={songs}
              onEnd={handleEnd}
              onWsError={setWsConnectionError}
              isEndingSession={isEndingSessionRef}
            />
          </div>
        ) : team ? (
          <Lobby
            team={team}
            sessions={sessions}
            onJoin={handleJoin}
            onCreate={handleCreate}
            onChangeTeam={() => {
              setTeam(null);
              setSelectedTeamId(null);
            }}
            theme={theme}
          />
        ) : null}
      </div>
    </div>
  );
};

export default CollabPage;
